import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, Plus, History, Notebook } from 'lucide-react';
import { PlanDuracionConfig, fetchPlanDuracionConfigs } from '../../../services/planconfigService';
import AddPlanConfigModal from '../../../components/ConfigPlan/AddPlanConfigModal';
import EditPlanConfigModal from '../../../components/ConfigPlan/EditPlanConfigModal';
import DeletePlanConfigModal from '../../../components/ConfigPlan/DeletePlanConfigModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const sortAscending = (configs: PlanDuracionConfig[]) =>
    [...configs].sort((a, b) => a.id - b.id);

const ExpandableDescription = ({ description }: { description?: string | null }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const words = (description || '').split(' ');
    const truncatedDescription = words.slice(0, 4).join(' ') + (words.length > 4 ? '...' : '');

    if (!description) return <span className="text-gray-400">Sin descripción</span>;

    return (
        <span
            onClick={() => words.length > 4 && setIsExpanded(!isExpanded)}
            className={words.length > 4 ? 'cursor-pointer hover:text-blue-600 transition-colors' : 'cursor-default'}
        >
            {isExpanded ? description : truncatedDescription}
        </span>
    );
};

const PlanConfigPage: React.FC = () => {
    const [configuraciones, setConfiguraciones] = useState<PlanDuracionConfig[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<PlanDuracionConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();

    // Auth & permissions
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('planduracionconfig', userLevelId);

    // Carga de configuraciones solo si tiene permiso de lectura
    useEffect(() => {
        const cargarConfiguraciones = async () => {
            try {
                if (perm?.can_read) {
                    const data = await fetchPlanDuracionConfigs();
                    const sortedData = sortAscending(data);   // orden ascendente por ID
                    setConfiguraciones(sortedData);
                } else {
                    setConfiguraciones([]);
                }
            } catch (error) {
                console.error("Error al cargar configuraciones de plan:", error);
                setConfiguraciones([]);
            }
        };
        if (perm) cargarConfiguraciones();
    }, [perm]);

    const handleCreated = (newConfig: PlanDuracionConfig) => {
        if (!perm?.can_read) return;
        setConfiguraciones(prev => sortAscending([...prev, newConfig]));
        setShowModal(false);
    };

    const handleUpdated = (updatedConfig: PlanDuracionConfig) => {
        setConfiguraciones(prev => {
            const updatedList = prev.map(c => c.id === updatedConfig.id ? updatedConfig : c);
            return sortAscending(updatedList);
        });
    };

    const handleDeleted = (deletedId: number) => {
        setConfiguraciones(prev => prev.filter(c => c.id !== deletedId));
    };

    // Paginación
    const totalPages = Math.ceil(configuraciones.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = configuraciones.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando configuración de planes..." />;

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Configuración de Planes</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/planconfig" className="text-[#c1348b] hover:text-[#8f1f64]">Configuración de Planes</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                        onClick={() => navigate('/plans/config/history')}
                    >
                        <History className="w-5 h-5" />
                    </button>

                    {perm.can_create && (
                        <button
                            onClick={() => setShowModal(true)}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Configuración
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <AddPlanConfigModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onCreated={handleCreated}
                />

                <EditPlanConfigModal
                    isOpen={editModalOpen}
                    config={selectedConfig}
                    onClose={() => setEditModalOpen(false)}
                    onUpdated={handleUpdated}
                />

                <DeletePlanConfigModal
                    isOpen={deleteModalOpen}
                    config={selectedConfig}
                    onClose={() => setDeleteModalOpen(false)}
                    onDeleted={handleDeleted}
                />

                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horas</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temporizador</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {perm.can_read && displayedConfigs.map((config) => (
                                <tr key={config.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 text-sm text-gray-900">{config.plan_nombre}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900 capitalize">{config.unidad}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{config.dias ?? '-'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{config.horas ?? '-'}</td>
                                    <td className="px-4 py-4">
                                        <span className={`px-2 inline-flex text-xs font-medium rounded-full ${
                                            config.usa_temporizador
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {config.usa_temporizador ? 'Sí' : 'No'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                        <ExpandableDescription description={config.descripcion} />
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                        {new Date(config.fecha_creacion).toLocaleDateString('es-ES', {
                                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                        })}
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className={`px-2 inline-flex text-xs font-medium rounded-full ${config.estado ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {config.estado ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex gap-2">
                                            {perm.can_update && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedConfig(config);
                                                        setEditModalOpen(true);
                                                    }}
                                                    className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                            {perm.can_delete && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedConfig(config);
                                                        setDeleteModalOpen(true);
                                                    }}
                                                    className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {perm.can_read && configuraciones.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">No hay configuraciones disponibles</span>
                                            {perm.can_create && <p className="text-gray-500 text-sm">Agrega una nueva configuración para comenzar</p>}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {perm.can_read && configuraciones.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={configuraciones.length}
                    />
                )}
            </div>
        </div>
    );
};

export default PlanConfigPage;

